// Single source of truth for case-study deployments across list + detail pages.

export const caseStudies = [
  {
    slug: "ghana",
    code: "GOCEMS",
    country: "Ghana",
    tag: "Industrial",
    logo: "/logos/gocems.svg",
    headline: "Nationwide industrial emissions monitoring with EPA Ghana",
    listSummary:
      "AOCEMS is live in partnership with the Environmental Protection Agency. The deployment delivers continuous, country-wide visibility into industrial air quality and effluent for regulators.",
    intro:
      "GOCEMS is the Ghana deployment of AOCEMS, operating in partnership with the Environmental Protection Agency. It establishes the digital backbone for continuous emissions oversight across industrial facilities country-wide.",
    partner: "Environmental Protection Agency (EPA)",
    framework: "Continuous emissions oversight under Ghana's environmental regulatory regime",
    status: "Live · Operating in production",
    coverage: [
      "Air quality at industrial sites",
      "Effluent discharge monitoring",
      "Compliance dashboards for regulators and operators",
    ],
    outcomes: [
      "Established a single source of truth for industrial emissions data",
      "Enabled automated, regulator-grade reporting",
      "Reduced reliance on manual field inspections",
    ],
    quote: '"Pollution can be managed if it is measured."',
    presentation: {
      title: "GOCEMS Programme Brochure · Jul 2025",
      file: "gocems-brochure.pdf",
      size: "2.1 MB",
    },
    statusTimeline: [
      { date: "Jul 29, 2025", milestone: "Launched", state: "done", note: "Live with EPA Ghana" },
      { date: "2025", milestone: "Country-wide rollout", state: "active", note: "Real-time industrial emissions monitoring" },
    ],
    deploymentPhase: "Live · Phase 1 (Africa)",
  },
  {
    slug: "kenya",
    code: "KOCEMS",
    country: "Kenya",
    tag: "Air Quality",
    logo: "/logos/kocems.svg",
    headline: "Anchored in the Air Quality Regulations 2024 with NEMA",
    listSummary:
      "Launched with the National Environment Management Authority, the Kenya programme codifies AOCEMS into the country's Air Quality regulatory framework.",
    intro:
      "KOCEMS is the Kenya deployment of AOCEMS, launched in partnership with the National Environment Management Authority. The programme codifies AOCEMS directly into the country's Air Quality regulatory framework.",
    partner: "National Environment Management Authority (NEMA)",
    framework: "Kenya Air Quality Regulations 2024",
    status: "Live · Air quality programme active",
    coverage: [
      "Ambient air quality monitoring",
      "Industrial point-source coverage",
      "Public-interest data infrastructure",
    ],
    outcomes: [
      "AOCEMS embedded in the national Air Quality framework",
      "Standardised data formats accepted by the regulator",
      "Foundation for cross-sector enforcement",
    ],
    quote: '"We cannot fix what we fail to measure."',
    video: {
      src: "/videos/kocems.mp4",
      title: "KOCEMS · Field Footage",
    },
    presentation: {
      title: "KOCEMS Inaugural Presentation",
      file: "kocems-presentation.pdf",
      size: "4.6 MB",
    },
    statusTimeline: [
      { date: "2024", milestone: "Anchored in Air Quality Regulations 2024", state: "done" },
      { date: "Mar 11, 2026", milestone: "Launched", state: "done", note: "Inaugural launch with NEMA" },
      { date: "2026", milestone: "Implementation in progress", state: "active", note: "Nationwide rollout per AQR 2024" },
    ],
    deploymentPhase: "Live · Phase 1 (Africa)",
  },
  {
    slug: "tanzania",
    code: "TOCEMS",
    country: "Tanzania",
    tag: "Multi-vector",
    logo: "/logos/tocems.svg",
    headline: "Joint deployment underway with NEMC",
    listSummary:
      "MOU signed with the National Environment Management Council. Site surveys, hardware procurement and DAS provisioning are in active execution.",
    intro:
      "TOCEMS is the Tanzania deployment of AOCEMS, advancing under an MOU with the National Environment Management Council. Site surveys, hardware procurement and DAS provisioning are in active execution.",
    partner: "National Environment Management Council (NEMC)",
    framework: "MOU under Tanzania's environmental management regime",
    status: "Pilot · Deployment underway",
    coverage: ["Air quality", "Effluent / water quality", "Sound / noise monitoring"],
    outcomes: [
      "MOU signed with the national regulator",
      "Phased rollout playbook activated",
      "Local engineering capacity onboarded",
    ],
    quote: '"Engineering climate intelligence — together with the regulator."',
    video: {
      src: "/videos/tocems.mp4",
      title: "TOCEMS · Field Footage",
    },
    presentation: {
      title: "TOCEMS Programme Presentation · Dec 2025",
      file: "tocems-presentation.pdf",
      size: "14.8 MB",
    },
    statusTimeline: [
      { date: "Dec 2025", milestone: "MOU signed with NEMC", state: "done" },
      { date: "2025–2026", milestone: "Site survey & DAS provisioning", state: "active", note: "Hardware procurement underway" },
      { date: "TBA", milestone: "Country-wide go-live", state: "pending" },
    ],
    deploymentPhase: "Pilot · Phase 1 (Africa)",
  },
  {
    slug: "nigeria",
    code: "NIMACEMS",
    country: "Nigeria",
    tag: "Maritime",
    logo: "/logos/nimacems.svg",
    headline: "MARPOL enforcement in partnership with NIMASA",
    listSummary:
      "Feasibility study complete. AOCEMS extends its monitoring stack to vessel emissions and port-air quality as a technical stakeholder of IMO GreenVoyage 2050.",
    intro:
      "NIMACEMS is the Nigeria maritime deployment of AOCEMS. The programme extends the platform's monitoring stack to vessel emissions and port-air quality, as a technical stakeholder of IMO GreenVoyage 2050.",
    partner: "Nigerian Maritime Administration and Safety Agency (NIMASA)",
    framework: "MARPOL · IMO GreenVoyage 2050",
    status: "Pilot · Feasibility complete · PPP process ongoing",
    coverage: ["Vessel exhaust monitoring", "Port air quality", "Meteorology fusion"],
    outcomes: [
      "Feasibility study complete",
      "Technical stakeholder of IMO GreenVoyage 2050",
      "Foundation for continental maritime enforcement",
    ],
    quote: '"Zero emissions, infinite possibilities — at sea and at port."',
    testimonial: {
      text: "NIMASA is advancing IMO energy transition strategies through digital innovation that turns ambition into measurable progress.",
      author: "Dr. Oma Ofodile",
      role: "NIMASA",
    },
    partnershipJourney: {
      title: "UPSG × NIMASA — Maritime Emissions Partnership",
      subtitle: "Pioneering Nigeria's Maritime Decarbonisation",
      intro:
        "UPSG has been actively engaged with the Nigerian Maritime Administration and Safety Agency (NIMASA) through a structured engagement process — from initial meetings and feasibility studies to now working jointly on a Public-Private Partnership (PPP) model for Nigeria's maritime emissions monitoring.",
      steps: [
        {
          num: "01",
          title: "Initial Meetings",
          desc:
            "UPSG and NIMASA held high-level engagement meetings to explore the deployment of a real-time maritime emissions monitoring system across Nigeria's ports and coastline.",
        },
        {
          num: "02",
          title: "Feasibility Study Completed",
          desc:
            "A comprehensive feasibility study was conducted, assessing Nigeria's maritime infrastructure, port coverage requirements, regulatory alignment with IMO frameworks, and technical integration pathways.",
        },
        {
          num: "03",
          title: "COP 30 Global Showcase",
          desc:
            "NIMASA showcased NMCEMS at COP 30 in Belém, validating the initiative on the global stage.",
        },
        {
          num: "04",
          title: "PPP Model in Development",
          desc:
            "UPSG and NIMASA are finalising a zero-upfront-cost PPP model funded through industry participation.",
        },
        {
          num: "05",
          title: "Next Steps",
          desc:
            "Full deployment will expand NMCEMS across major ports and integrate with NNPC coastal terminal monitoring.",
        },
      ],
    },
    presentation: {
      title: "NIMACEMS Presentation · December 9th",
      file: "nimacems-presentation.pdf",
      size: "5.2 MB",
    },
    statusTimeline: [
      { date: "2024–25", milestone: "Initial engagement meetings", state: "done", note: "High-level talks with NIMASA on real-time maritime emissions monitoring" },
      { date: "2025", milestone: "Feasibility study completed", state: "done", note: "Ports, IMO alignment, technical integration pathways assessed" },
      { date: "Nov 2025", milestone: "COP 30 global showcase", state: "done", note: "NIMASA showcased NMCEMS at COP 30 in Belém" },
      { date: "Now", milestone: "PPP model in development", state: "active", note: "Zero-upfront-cost PPP, funded through industry participation" },
      { date: "2026", milestone: "Full deployment across major ports", state: "pending", note: "Integration with NNPC coastal terminal monitoring" },
    ],
    deploymentPhase: "Pilot · Phase 1 (Africa)",
  },
];

export const caseStudyBySlug = (slug) => caseStudies.find((c) => c.slug === slug);
